import React, { useState, useEffect } from 'react';
import { Button, TextField, Box, Flex, Text, Heading, Card, Badge } from '@radix-ui/themes';
import * as math from 'mathjs';
import FunctionSettingsDialog from './FunctionSettingsDialog';

function UseFunctionForm({ functions, onCalculate, onEdit, onDelete, setExecutionResults, onUpdateFunction, allTags = [], selectedFunction, setSelectedFunction }) {
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [variableValues, setVariableValues] = useState({});
  const [variableUnits, setVariableUnits] = useState({});
  const [selectedTag, setSelectedTag] = useState(null);

  const filteredFunctions = selectedTag
    ? functions.filter(f => f.settings?.tags?.includes(selectedTag))
    : functions;

  const renderFunctionList = () => {
    if (filteredFunctions.length === 0) {
      return <Text>No functions found with the selected tag.</Text>;
    }

    const sortedFunctions = [...filteredFunctions].sort((a, b) => a.name.localeCompare(b.name));

    return sortedFunctions.map(func => (
      <Box key={func.name} style={{ marginTop: '4px' }}>
        <Button onClick={() => handleSelectFunction(func)} variant="soft" style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text size="3">{func.name}</Text>
          <Flex gap="2">
            {func.settings?.tags?.map(tag => (
              <Badge key={tag} color="gray">{tag}</Badge>
            ))}
          </Flex>
        </Button>
      </Box>
    ));
  };

  useEffect(() => {
    if (selectedFunction) {
      const newVariableValues = {};
      const newVariableUnits = {};
      const allVars = new Set();
      const mathjsKeywords = new Set([...Object.keys(math), 'sqrt', 'sin', 'cos', 'tan', 'log', 'exp']);
      const subFunctionNames = new Set(selectedFunction.subFunctions?.map(sf => sf.name) || []);
  
      const processVariablesString = (varsString) => {
        if (!varsString) return;
        const variableRegex = /([\w@.-]+)\s*(?:\(([^)]+)\))?/;
        varsString.split(',').forEach(v => {
          const trimmedV = v.trim();
          if (trimmedV) {
            const match = trimmedV.match(variableRegex);
            if (match) {
              const varName = match[1];
              const unit = match[2];
              allVars.add(varName);
              if (unit) {
                newVariableUnits[varName] = unit;
              }
            } else {
              allVars.add(trimmedV);
            }
          }
        });
      };

      // Collect explicitly defined variables
      if (selectedFunction.variables) {
        processVariablesString(selectedFunction.variables);
      }
      selectedFunction.subFunctions?.forEach(sf => {
        if (sf.variables) {
          processVariablesString(sf.variables);
        }
      });

      // Collect variables from expressions
      const allExpressions = [selectedFunction.expression, ...selectedFunction.subFunctions?.map(sf => sf.expression) || []];

      const placeholderRegex = /\{([\w@.-]+)\}/g;
      allExpressions.forEach(expr => {
        if (!expr) return;
        
        let match;
        while ((match = placeholderRegex.exec(expr)) !== null) {
          allVars.add(match[1]);
        }

        // Sanitize for math.js parsing to find other symbols
        let sanitizedExpr = expr.replace(placeholderRegex, '1');

        try {
            const node = math.parse(sanitizedExpr);
            node.traverse(function (n) {
                if (n.isSymbolNode && !mathjsKeywords.has(n.name)) {
                    allVars.add(n.name);
                }
            });
        } catch (e) {
            console.warn(`Expression parsing error in UseFunctionForm: ${e.message}`);
        }
      });
      
      allVars.forEach(v => {
        if (v && !subFunctionNames.has(v)) {
          newVariableValues[v] = '';
        }
      });
  
      setVariableValues(newVariableValues);
      setVariableUnits(newVariableUnits);
    } else {
      setVariableValues({});
      setVariableUnits({});
    }
  }, [selectedFunction]);

  const handleCalculate = () => {
    const numericVariableValues = Object.fromEntries(
      Object.entries(variableValues).map(([key, value]) => [key, parseFloat(value) || 0])
    );
    onCalculate(selectedFunction, numericVariableValues);
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(selectedFunction, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${selectedFunction.name}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleSaveSettings = (updatedSettings) => {
    const updatedFunction = {
      ...selectedFunction,
      settings: updatedSettings,
    };
    onUpdateFunction(updatedFunction);
    setSelectedFunction(updatedFunction);
  };

  const handleSelectFunction = (func) => {
    setSelectedFunction(func);
    setExecutionResults([]);
  };

  const renderVariableInputs = () => {
    const varsToRender = Object.keys(variableValues);
    const filteredVars = varsToRender.filter(v => v !== 'nestedResult');

    return filteredVars.map(v => (
      <label key={v} style={{ display: "grid", gridTemplateColumns: "1fr 2fr", alignItems: "center", gap: '8px' }}>
        <Text as="div" size="3" weight="bold">
          {v}
        </Text>
        <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "1fr auto", alignItems: "center" }}>
          <TextField.Root
            type="number"
            value={variableValues[v]}
            onChange={e => setVariableValues({ ...variableValues, [v]: e.target.value })}
          />
          <Text style={{ minWidth: '3rem' }} as="div" size="5" color="gray">
            {variableUnits[v] || ''}
          </Text>
        </div>
      </label>
    ));
  };

  return (
    <Box>
      {!selectedFunction ? (
        <Heading as="h2" size="4" mb="4">Use a Function</Heading>
      ) : (
         <></>
      )
      }
      
      {!selectedFunction ? (
        <>
          <Flex gap="2" mb="4" wrap="wrap">
            {allTags.map(tag => (
              <Button
                key={tag}
                variant={selectedTag === tag ? 'solid' : 'soft'}
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </Button>
            ))}
            {selectedTag && (
              <Button variant="ghost" onClick={() => setSelectedTag(null)}>Clear</Button>
            )}
          </Flex>
          <Flex direction="column" gap="2">
            {renderFunctionList()}
          </Flex>
        </>
      ) : (
        <Flex direction="column" gap="3">
          <Card>
            <Flex justify="between" align="center">
              <Box>
                <Text weight="bold" size="6">{selectedFunction.name}</Text>
              </Box>
              <Flex gap="2">
                {selectedFunction.settings?.tags?.map(tag => (
                  <Badge key={tag} color="gray">{tag}</Badge>
                ))}
              </Flex>
            </Flex>
            {selectedFunction.notes && (
              <Box mt="2" p="2" style={{ background: 'var(--yellow-a2)', borderRadius: 'var(--radius-2)'}}>
                <Text size="2" italic>{selectedFunction.notes}</Text>
              </Box>
            )}
          </Card>

          <Flex direction="column" gap="2">
            {renderVariableInputs()}
          </Flex>
          
          <Flex gap="3" mt="2">
            <Button onClick={handleCalculate}>Calculate</Button>
            <Button variant="soft" onClick={() => {
              setSelectedFunction(null);
              setExecutionResults([]);
            }}>Cancel</Button>
            <Button variant="outline" onClick={() => onEdit(selectedFunction)}>Edit</Button>
            <Button variant="outline" onClick={handleExport}><span className="material-symbols-outlined">upload</span></Button>
            <Button variant="outline" onClick={() => setSettingsOpen(true)}><span className="material-symbols-outlined">settings</span></Button>
          </Flex>
        </Flex>
      )}
      {selectedFunction && (
        <FunctionSettingsDialog
          open={isSettingsOpen}
          onOpenChange={setSettingsOpen}
          functionData={selectedFunction}
          onSave={handleSaveSettings}
          onDelete={onDelete}
          allTags={allTags}
        />
      )}
    </Box>
  );
}

export default UseFunctionForm;
